import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import * as Sentry from "@sentry/react";
import queryString from "query-string";

import DashboardLayout from "js/components/DashboardLayout";
import FullScreenLayout from "js/components/FullScreenLayout";
import AuthMiddleware from "js/components/Middleware/AuthMiddleware";
import ContractPermissionGuard from "js/components/Middleware/ContractPermissionGuard";
import ApiUsersConfig from "js/components/Organization/ApiUsersConfig";

import Auth from "js/pages/Auth/Auth";
import VerifyAccount from "js/pages/Auth/VerifyAccount";
import VerifyAccountAnonymous from "js/pages/Auth/VerifyAccountAnonymous";
import Dashboard from "js/pages/Dashboard/Dashboard";
import DeactivatedPage from "js/pages/DeactivatedPage";
import MembershipPermission from "js/pages/MembershipPermission";
import NotFound from "js/pages/NotFound";
import PaymentPage from "js/pages/Payments/PaymentPage";
import ChangePassword from "js/pages/Profile/ChangePassword";
import PasswordReset from "js/pages/Profile/PasswordReset";
import Profile from "js/pages/Profile/Profile";
import Signature from "js/pages/Tenants/Signature";
import ThankYouPage from "js/pages/ThankYouPage";
import Unauthorized from "js/pages/Unauthorized";

import { initializeSessionStorage } from "js/utils/sessionStorage";

import "styles/global.scss";

import { SentryErrorFallback } from "./components/SentryErrorBoundary";
import { useCookieWatch } from "./hooks/useCookieWatch";
import Contracts, { CONTRACT_EDITOR_PATHS, ContractEditor } from "./router/Contracts";
import Landlord from "./router/Landlord";
import MembershipRouter from "./router/MembershipRouter";
import Organization from "./router/Organization";
import Property from "./router/Property";
import Report from "./router/Report";
import Tenant from "./router/Tenant";
import permissions from "./utils/membershipPermissions";
import { safeLocalStorage } from "./utils/safeStorage";

// Wrap Routes with Sentry for automatic transaction naming
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App = () => {
  const location = useLocation();
  useCookieWatch();

  useEffect(() => {
    onParseQueryInvite();
    // Initialize session storage cleanup for tenant reports
    initializeSessionStorage();
  }, []);

  const onParseQueryInvite = (): void => {
    const queryParams = queryString.parse(location.search);
    const inviteToken = queryParams.invite;

    if (!!inviteToken) {
      safeLocalStorage.setItem("invite_token", inviteToken.toString());
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={SentryErrorFallback}
      showDialog
      onError={(error, errorInfo) => {
        console.error("Application error:", error, errorInfo);
      }}
    >
      <SentryRoutes>
        <Route index path="/login" element={<Auth />} />
        {["/login", "/signup", "/forgot-password"].map((path) => (
          <Route path={path} element={<Auth />} key={path} />
        ))}
        <Route path="/reports/*" element={<Report />} />
        <Route element={<DashboardLayout />}>
          <Route
            path="/"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <Dashboard />
              </AuthMiddleware>
            }
          />
          <Route
            path="/contracts/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <ContractPermissionGuard>
                  <Contracts />
                </ContractPermissionGuard>
              </AuthMiddleware>
            }
          />
          <Route
            path="/tenants/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <Tenant />
              </AuthMiddleware>
            }
          />
          <Route
            path="/properties/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <MembershipPermission
                  requiredPermission={permissions.propertyRegistration}
                >
                  <Property />
                </MembershipPermission>
              </AuthMiddleware>
            }
          />
          <Route
            path="/landlords/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <MembershipPermission
                  requiredPermission={permissions.propertyRegistration}
                >
                  <Landlord />
                </MembershipPermission>
              </AuthMiddleware>
            }
          />
          <Route
            path="/organizations/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <Organization />
              </AuthMiddleware>
            }
          />
          <Route
            path="/memberships/*"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <MembershipRouter />
              </AuthMiddleware>
            }
          />
          <Route
            path="/developers"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={true}
              >
                <ApiUsersConfig />
              </AuthMiddleware>
            }
          />
          <Route
            path="/profile"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={false}
              >
                <Profile />
              </AuthMiddleware>
            }
          />
          <Route
            path="/change-password"
            element={
              <AuthMiddleware
                requiresAuthentication={true}
                requiresValidation={false}
              >
                <ChangePassword />
              </AuthMiddleware>
            }
          />
        </Route>
        {/* El editor de contratos, a pantalla completa: sin el menú ni el encabezado del tablero. */}
        <Route element={<FullScreenLayout />}>
          {CONTRACT_EDITOR_PATHS.map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <AuthMiddleware
                  requiresAuthentication={true}
                  requiresValidation={true}
                >
                  <ContractPermissionGuard>
                    <ContractEditor />
                  </ContractPermissionGuard>
                </AuthMiddleware>
              }
            />
          ))}
        </Route>
        <Route
          path="/reset-password/:uidb64/:token"
          element={<PasswordReset />}
        />
        <Route
          path="/verify-account/:verificationToken"
          element={<VerifyAccountAnonymous />}
        />
        <Route
          path="/verify-account"
          element={
            <AuthMiddleware
              requiresAuthentication={true}
              requiresValidation={false}
            >
              <VerifyAccount />
            </AuthMiddleware>
          }
        />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/thanks" element={<ThankYouPage />} />
        <Route path="/deactivated" element={<DeactivatedPage />} />
        <Route path="/pay/:type/:id" element={<PaymentPage />} />
        <Route path="/tenants/signature" element={<Signature />} />
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </Sentry.ErrorBoundary>
  );
};

export default App;
